import React from 'react';
import {
  Row,
  Col,
  CardBody,
  Card,
  Alert,
  Container,
  Form,
  Input,
  FormFeedback,
  Label,
} from 'reactstrap';
import { useNavigate } from 'react-router-dom';

import * as Yup from 'yup';
import { useFormik } from 'formik';
import { useRegisterMutation } from 'helpers/http-client/hooks/authHooks';
import { RegisterDto } from 'helpers/jwt-token-access/authTypes';
import { getFormErrors } from 'helpers/http-client/apiClient';

//meta title

const Register: React.FC = () => {
  const navigate = useNavigate();
  const { mutate: register, isPending, error } = useRegisterMutation();

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      email: '',
      password: '',
      firstName: '',
      lastName: '',
    } as RegisterDto,
    validationSchema: Yup.object({
      email: Yup.string().email('Invalid email address').required('Please Enter Your Email'),
      password: Yup.string()
        .min(6, 'Password must be at least 6 characters')
        .required('Please Enter Your Password'),
      firstName: Yup.string().required('Please Enter Your First Name'),
      lastName: Yup.string().required('Please Enter Your Last Name'),
    }),
    onSubmit: (values) => {
      register(values, {
        onSuccess: () => {
          navigate('/dashboard');
        },
      });
    },
  });

  return (
    <React.Fragment>
      <div className="account-pages my-5 pt-sm-5">
        <Container>
          <Row className="justify-content-center">
            <Col md={8} lg={6} xl={5}>
              <Card className="overflow-hidden">
                <div className="bg-primary-subtle">
                  <Row>
                    <Col xs={7}>
                      <div className="text-primary p-4">
                        <h5 className="text-primary">Create Account</h5>
                        <p>Get your free Pharmacy Management account now.</p>
                      </div>
                    </Col>
                  </Row>
                </div>
                <CardBody className="pt-0">
                  <div className="p-2">
                    <Form
                      className="form-horizontal"
                      onSubmit={(e) => {
                        e.preventDefault();
                        validation.handleSubmit();
                        return false;
                      }}
                    >
                      {error && (
                        <Alert color="danger">
                          {getFormErrors(error) || 'Registration failed'}
                        </Alert>
                      )}

                      <div className="mb-3">
                        <Label className="form-label">Email</Label>
                        <Input
                          id="email"
                          name="email"
                          className="form-control"
                          placeholder="Enter email"
                          type="email"
                          onChange={validation.handleChange}
                          onBlur={validation.handleBlur}
                          value={validation.values.email}
                          invalid={validation.touched.email && !!validation.errors.email}
                        />
                        {validation.touched.email && validation.errors.email ? (
                          <FormFeedback type="invalid">{validation.errors.email}</FormFeedback>
                        ) : null}
                      </div>

                      <div className="mb-3">
                        <Label className="form-label">First Name</Label>
                        <Input
                          id="firstName"
                          name="firstName"
                          type="text"
                          placeholder="Enter first name"
                          onChange={validation.handleChange}
                          onBlur={validation.handleBlur}
                          value={validation.values.firstName}
                          invalid={validation.touched.firstName && !!validation.errors.firstName}
                        />
                        {validation.touched.firstName && validation.errors.firstName ? (
                          <FormFeedback type="invalid">{validation.errors.firstName}</FormFeedback>
                        ) : null}
                      </div>

                      <div className="mb-3">
                        <Label className="form-label">Last Name</Label>
                        <Input
                          id="lastName"
                          name="lastName"
                          type="text"
                          placeholder="Enter last name"
                          onChange={validation.handleChange}
                          onBlur={validation.handleBlur}
                          value={validation.values.lastName}
                          invalid={validation.touched.lastName && !!validation.errors.lastName}
                        />
                        {validation.touched.lastName && validation.errors.lastName ? (
                          <FormFeedback type="invalid">{validation.errors.lastName}</FormFeedback>
                        ) : null}
                      </div>

                      <div className="mb-3">
                        <Label className="form-label">Password</Label>
                        <Input
                          id="password"
                          name="password"
                          type="password"
                          placeholder="Enter password"
                          onChange={validation.handleChange}
                          onBlur={validation.handleBlur}
                          value={validation.values.password}
                          invalid={validation.touched.password && !!validation.errors.password}
                        />
                        {validation.touched.password && validation.errors.password ? (
                          <FormFeedback type="invalid">{validation.errors.password}</FormFeedback>
                        ) : null}
                      </div>

                      <div className="mt-4 d-grid">
                        <button
                          className="btn btn-primary btn-block"
                          type="submit"
                          disabled={isPending}
                        >
                          {isPending ? 'Registering...' : 'Register'}
                        </button>
                      </div>

                      <div className="mt-4 text-center">
                        <p className="mb-0">
                          By registering you agree to the Pharmacy Management{' '}
                          <a href="#" className="text-primary">
                            Terms of Use
                          </a>
                        </p>
                      </div>
                    </Form>
                  </div>
                </CardBody>
              </Card>
              <div className="mt-5 text-center">
                <p>
                  Already have an account?{' '}
                  <a href="/login" className="fw-medium text-primary">
                    Login
                  </a>
                </p>
                <p>
                  © {new Date().getFullYear()} Pharmacy Management. Crafted with{' '}
                  <i className="mdi mdi-heart text-danger" /> by Your Team
                </p>
              </div>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default Register;
